import { Building2, ChevronDown, Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import httpService from "../../../../services/httpService";

interface Provider {
  id: string;
  name: string;
  logoUrl?: string;
}

interface ProviderSelectorProps {
  selectedProviderId: string | null;
  onProviderChange: (providerId: string) => void;
}

const ProviderSelector: React.FC<ProviderSelectorProps> = ({
  selectedProviderId,
  onProviderChange,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await httpService.get<{ providers: Provider[] }>(
        "/providers"
      );
      setProviders(response.data.providers || []);
      // Auto-select first provider if none selected
      if (!selectedProviderId && response.data.providers?.length > 0) {
        onProviderChange(response.data.providers[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch providers:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedProvider = providers.find((p) => p.id === selectedProviderId);

  const filteredProviders = providers.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <label
        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}
      >
        Provider
      </label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
          isDarkMode
            ? "bg-rp-surface border-rp-separator text-rp-grey hover:border-rp-grey/40"
            : "bg-white border-gray-300 text-gray-900 hover:border-gray-400"
        } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <div className="flex items-center space-x-3">
          {selectedProvider?.logoUrl ? (
            <img
              src={selectedProvider.logoUrl}
              alt={selectedProvider.name}
              className="w-6 h-6 rounded object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div
              className={`w-6 h-6 rounded flex items-center justify-center ${
                isDarkMode ? "bg-rp-surface-raised" : "bg-gray-100"
              }`}
            >
              <Building2
                className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-400"}`}
              />
            </div>
          )}
          <span>
            {loading
              ? "Loading..."
              : selectedProvider?.name || "Select a provider"}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Search input */}
          <div className="p-2">
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                }`}
              />
              <input
                type="text"
                placeholder="Search providers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-9 pr-3 py-2 rounded-md text-sm ${
                  isDarkMode
                    ? "bg-rp-surface text-rp-grey placeholder-rp-grey/40 border-rp-separator"
                    : "bg-gray-50 text-gray-900 placeholder-gray-500 border-gray-200"
                } border focus:outline-none focus:ring-2 focus:ring-teal-500`}
              />
            </div>
          </div>

          {/* Provider list */}
          <div className="max-h-60 overflow-y-auto">
            {filteredProviders.length === 0 ? (
              <div
                className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
              >
                No providers found
              </div>
            ) : (
              filteredProviders.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => {
                    onProviderChange(provider.id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-colors ${
                    selectedProviderId === provider.id
                      ? isDarkMode
                        ? "bg-teal-900/30 text-teal-400"
                        : "bg-teal-50 text-teal-700"
                      : isDarkMode
                        ? "hover:bg-rp-surface-raised text-rp-grey"
                        : "hover:bg-gray-50 text-gray-900"
                  }`}
                >
                  {provider.logoUrl ? (
                    <img
                      src={provider.logoUrl}
                      alt={provider.name}
                      className="w-6 h-6 rounded object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div
                      className={`w-6 h-6 rounded flex items-center justify-center ${
                        isDarkMode ? "bg-rp-surface-raised" : "bg-gray-100"
                      }`}
                    >
                      <Building2
                        className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-400"}`}
                      />
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium">{provider.name}</div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
                    >
                      ID: {provider.id}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderSelector;

